import { useEffect, useRef, useState } from 'react'
import { faHouse } from '@fortawesome/pro-duotone-svg-icons'

import Icon from '../icon/Icon'
import {
  Card,
  HeroImage,
  HeroPlaceholder,
  StatusPill,
  MatchPill,
  Body,
  SenderRow,
  SenderDot,
  Title,
  MetaLine,
  PriceLine,
  Message,
  Reasons,
  ReasonChip,
  PrivacyNote,
  Actions,
  InterestedButton,
  PassButton,
  ResolvedNote,
  SourceLink,
} from './styled'
import { formatMonthlyPrice } from '../../_utilities/format-price'
import {
  formatBedBath,
  isResponded,
  resolvedNoteFor,
  statusLabel,
  statusToTone,
} from './helpers'
import { PingCardProps } from './types'

/**
 * Renter-side ping card. Shows the matched listing the property
 * manager pinged about + match reasons + Interested / Pass actions.
 * The renter's identity is never visible to the sender — the privacy
 * note inside the card makes that explicit.
 *
 * @param {PingCardProps} props
 * @returns {JSX.Element}
 */
const PingCard = ({ ping, onView, onRespond }: PingCardProps): JSX.Element => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const hasFiredViewRef = useRef<boolean>(false)

  useEffect(() => {
    if (
      ping.status === 'pending' &&
      hasFiredViewRef.current === false &&
      onView !== undefined
    ) {
      hasFiredViewRef.current = true
      onView(ping.id)
    }
  }, [ping.id, ping.status, onView])

  const handleResponse = async (
    response: 'interested' | 'not-interested',
  ): Promise<void> => {
    if (isSubmitting === true) {
      return
    }
    setIsSubmitting(true)
    try {
      await onRespond(ping.id, response)
    } catch (error) {
      console.error('[ping-card] respond failed', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const responded = isResponded(ping.status)
  const heroStyle =
    ping.listing.imageUrl !== null
      ? { backgroundImage: `url(${ping.listing.imageUrl})` }
      : undefined

  return (
    <Card>
      <HeroImage style={heroStyle}>
        {ping.listing.imageUrl === null ? (
          <HeroPlaceholder>
            <Icon icon={faHouse} />
          </HeroPlaceholder>
        ) : null}
        <StatusPill tone={statusToTone(ping.status)}>
          {statusLabel(ping.status)}
        </StatusPill>
        <MatchPill>{ping.matchScore}/100 match</MatchPill>
      </HeroImage>

      <Body>
        <SenderRow>
          <SenderDot />
          {ping.senderClientName} sent this to a few good matches
        </SenderRow>

        <Title>{ping.listing.title}</Title>

        <MetaLine>
          {[
            ping.listing.fullAddress ?? ping.listing.city,
            formatBedBath(ping.listing.bedrooms, ping.listing.bathrooms),
            ping.listing.propertyType,
          ]
            .filter(value => value !== null && value !== '')
            .join(' · ')}
        </MetaLine>

        <PriceLine>
          {formatMonthlyPrice(
            ping.listing.priceMonthly,
            ping.listing.priceCurrency,
            { nullFallback: 'Price on request' },
          )}
        </PriceLine>

        {ping.message !== '' ? <Message>{ping.message}</Message> : null}

        {ping.matchReasons.length > 0 ? (
          <Reasons>
            {ping.matchReasons.map((reason, index) => (
              <ReasonChip key={`${reason}-${index}`}>{reason}</ReasonChip>
            ))}
          </Reasons>
        ) : null}

        {ping.senderListingUrl !== null ? (
          <SourceLink
            href={ping.senderListingUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Visit listing ↗
          </SourceLink>
        ) : null}

        <PrivacyNote>
          {ping.senderClientName} can&apos;t see who you are. Saying
          &ldquo;Interested&rdquo; will share your contact info — passing keeps
          you anonymous.
        </PrivacyNote>

        {responded === false ? (
          <Actions>
            <InterestedButton
              type="button"
              onClick={() => handleResponse('interested')}
              disabled={isSubmitting === true}
            >
              I&apos;m interested
            </InterestedButton>
            <PassButton
              type="button"
              onClick={() => handleResponse('not-interested')}
              disabled={isSubmitting === true}
            >
              Pass
            </PassButton>
          </Actions>
        ) : (
          <ResolvedNote>{resolvedNoteFor(ping.status)}</ResolvedNote>
        )}
      </Body>
    </Card>
  )
}

export default PingCard
